<nz-collapse [nzAccordion]="true" [nzBordered]="false">
  @for (panel of panels; track panel.id) {
    <nz-collapse-panel [nzHeader]="panel.name" [nzActive]="false">
      @switch (panel.id) {
        @case (DisputeType.INITIAL_ATTEMPT) {
          <div class="space-y-4">
            <p class="text-base text-text-primary">
              This is the <strong>preferred and most effective way</strong> to resolve a dispute. Both parties are encouraged to communicate directly and work toward a <strong>mutual agreement</strong> on how the dispute should be resolved. In most cases, disputes can be settled at this stage without involving third parties, allowing the project to continue with minimal disruption.
            </p>
            
            <div>
              <p class="text-base text-text-primary font-semibold mb-2">When engaging in initial resolution:</p>
              <ul class="list-disc pl-6 space-y-2 text-base text-text-primary">
                <li>Clearly explain what went wrong and why the dispute was raised.</li>
                <li>Be specific about the outcome you are seeking (e.g. revised deliverables, corrections, or completion of work).</li>
                <li>Engage constructively and in good faith to reach a shared understanding.</li>
              </ul>
            </div>

            <p class="text-base text-text-primary">
              To resolve the dispute at this stage, parties should use the <strong>Mutual Resolution</strong> feature on Handshake.
            </p>

            <div>
              <p class="text-base text-text-primary font-semibold mb-2">Once both parties accept the mutual resolution:</p>
              <ul class="list-disc pl-6 space-y-2 text-base text-text-primary">
                <li>The dispute will be marked as resolved.</li>
                <li>The project will be <strong>unlocked</strong>, allowing work to resume.</li>
                <li><strong>No funds are released automatically</strong> at this stage.</li>
              </ul>
            </div>

            <p class="text-base text-text-primary">
              If the parties have agreed on a release of funds: Please <strong>re-submit a separate request for release of funds</strong> under "Project Progress" reflecting the agreed terms.
            </p>

            <p class="text-base text-text-primary">
              If the agreement involves a refund: Please contact Handshake Support at <a href="mailto:contact&#64;handshake.finance" class="text-blue-600 hover:underline"><strong>contact&#64;handshake.finance</strong></a> for assistance.
            </p>
          </div>
        }
        @case (DEFECT_ASSESSMENT_PANEL_ID) {
          <div class="space-y-4">
            <p class="text-base text-text-primary">
              Together with <strong>Aces Programme</strong> (<a [href]="acesProgrammeUrl" target="_blank" rel="noopener noreferrer" class="text-blue-600 hover:underline break-all">agcare.com.sg</a>), Handshake can arrange a <strong>licensed, completely neutral third party</strong> to assess defects and disputes. Handshake covers certain costs. Reach out to the Handshake team to find out more.
            </p>

            <div>
              <p class="text-base text-text-primary font-semibold mb-2">Contact the Handshake team:</p>
              <ul class="list-none pl-0 space-y-2 text-base text-text-primary">
                <li>
                  <strong>WhatsApp: </strong>
                  <a href="https://wa.me/6580778295" target="_blank" rel="noopener noreferrer" class="text-blue-600 hover:underline">+65 8077 8295</a>
                </li>
                <li>
                  <strong>Email: </strong>
                  <a href="mailto:contact&#64;handshake.finance" class="text-blue-600 hover:underline break-all">contact&#64;handshake.finance</a>
                </li>
              </ul>
            </div>
          </div>
        }
        @case (DisputeType.MEDIATION) {
          <div class="space-y-4">
            <p class="text-base text-text-primary">
              If direct discussions do not result in agreement, parties may choose to resolve the dispute through <strong>mediation</strong>.
            </p>

            <p class="text-base text-text-primary">
              Mediation involves a neutral third party who facilitates structured discussions to help both sides reach a mutually acceptable outcome. Parties may choose:
            </p>

            <ul class="list-disc pl-6 space-y-2 text-base text-text-primary">
              <li>
                The <strong>Singapore Mediation Centre (SMC)</strong> – a leading mediation institution in Singapore offering neutral, structured mediation services. Learn more at <a href="https://www.smc.org.sg" target="_blank" rel="noopener noreferrer" class="text-blue-600 hover:underline">https://www.smc.org.sg</a>.
              </li>
              @if (showCaseTrustBrand) {
                <li>
                  <strong>CaseTrust-approved mediation</strong>, where applicable, for disputes relating to CaseTrust-covered industries. Learn more at <a href="https://www.casetrust.org.sg" target="_blank" rel="noopener noreferrer" class="text-blue-600 hover:underline">https://www.casetrust.org.sg</a>.
                </li>
              }
            </ul>

            <p class="text-base text-text-primary">
              The mediator does not impose a decision; any settlement is reached by mutual agreement between the parties.
            </p>

            <div>
              <p class="text-base text-text-primary font-semibold mb-2">Once both parties accept the signed settlement agreement:</p>
              <ul class="list-disc pl-6 space-y-2 text-base text-text-primary">
                <li>The dispute will be marked as resolved.</li>
                <li>The project will be <strong>unlocked</strong>, allowing work to resume.</li>
                <li><strong>No funds are released automatically</strong> at this stage.</li>
              </ul>
            </div>

            <div>
              <p class="text-base text-text-primary font-semibold mb-2">If the parties have agreed on a release of funds:</p>
              <p class="text-base text-text-primary">
                Please <strong>re-submit a separate request for release of funds</strong> under "Project Progress" reflecting the agreed terms.
              </p>
            </div>

            <div>
              <p class="text-base text-text-primary font-semibold mb-2">If the agreement involves a refund:</p>
              <p class="text-base text-text-primary">
                Please contact Handshake Support at <a href="mailto:contact&#64;handshake.finance" class="text-blue-600 hover:underline"><strong>contact&#64;handshake.finance</strong></a> for assistance.
              </p>
            </div>
          </div>
        }
        @case (DisputeType.ARBITRATION) {
          <div class="space-y-4">
            <p class="text-base text-text-primary">
              If mediation is unsuccessful or not pursued, disputes may be resolved through <strong>arbitration</strong>. Arbitration is a formal dispute resolution process administered by an arbitral institution such as the <strong>Singapore International Arbitration Centre (SIAC)</strong> or another mutually agreed body. An arbitrator or tribunal reviews the submissions from both parties and issues a decision.
            </p>

            <div>
              <p class="text-base text-text-primary font-semibold mb-2">Important considerations:</p>
              <ul class="list-disc pl-6 space-y-2 text-base text-text-primary">
                <li>Arbitration is conducted under Singapore law (either the Arbitration Act or International Arbitration Act, as applicable).</li>
                <li>The arbitral decision is <strong>final and legally binding</strong> on both parties.</li>
              </ul>
            </div>

            <div>
              <p class="text-base text-text-primary font-semibold mb-2">After arbitration concludes:</p>
              <ul class="list-disc pl-6 space-y-2 text-base text-text-primary">
                <li>Upload the signed arbitral award or settlement document as a settlement agreement.</li>
                <li>Funds will be released strictly in accordance with the decision.</li>
              </ul>
            </div>

            <p class="text-base text-text-primary">
              Please contact Handshake Support at <a href="mailto:contact&#64;handshake.finance" class="text-blue-600 hover:underline"><strong>contact&#64;handshake.finance</strong></a> for assistance.
            </p>
          </div>
        }
        @case (DisputeType.LITIGATION) {
          <div class="space-y-4">
            <p class="text-base text-text-primary">
              As a last resort, disputes may be resolved through <strong>court proceedings</strong> in Singapore.
            </p>

            <p class="text-base text-text-primary">
              Depending on the claim amount and circumstances, disputes may be brought before the <strong>Singapore State Courts or High Court</strong> and will proceed under the <strong>Rules of Court 2021</strong>.
            </p>

            <div>
              <p class="text-base text-text-primary font-semibold mb-2">If a court judgment is issued or a court-recorded settlement is reached:</p>
              <ul class="list-disc pl-6 space-y-2 text-base text-text-primary">
                <li>Upload the signed judgment or settlement document as a settlement agreement.</li>
                <li>Funds will be released in accordance with the court's decision.</li>
              </ul>
            </div>

            <p class="text-base text-text-primary">
              Please contact Handshake Support at <a href="mailto:contact&#64;handshake.finance" class="text-blue-600 hover:underline"><strong>contact&#64;handshake.finance</strong></a> for assistance.
            </p>
          </div>
        }
      }
    </nz-collapse-panel>
  }

  <!-- How Can Handshake Help You Section -->
  <nz-collapse-panel nzHeader="How Can Handshake Help You" [nzActive]="false">
      <div class="space-y-4">
        <p class="text-base text-text-primary">
          Handshake acts as a <strong>neutral facilitator</strong> throughout the dispute resolution process. While we do not make judgments or take sides, we provide guidance, resources, and coordination support to help both parties move the dispute toward resolution.
        </p>

        <div>
          <p class="text-base text-text-primary font-semibold mb-2">What we can assist with:</p>
          <ul class="list-disc pl-6 space-y-2 text-base text-text-primary">
            <li>
              <strong>Negotiation support:</strong> We can help arrange a meeting room for negotiations between both parties at no additional cost.
            </li>
            <li>
              <strong>Settlement agreement templates:</strong> We can provide a sample settlement agreement to help document mutually agreed outcomes clearly.
            </li>
            <li>
              <strong>Mediation referrals:</strong> If mediation is required, we can connect you with professional service mediators{{ showCaseTrustBrand ? ', such as CaseTrust,' : '' }} to facilitate the dispute.
            </li>
            <li>
              <strong>Legal introductions:</strong> Where necessary, we can introduce you to a panel of independent lawyers for further legal assistance.
            </li>
          </ul>
        </div>

        <div>
          <p class="text-base text-text-primary font-semibold mb-2">Important to note:</p>
          <ul class="list-disc pl-6 space-y-2 text-base text-text-primary">
            <li>Handshake does <strong>not</strong> determine who is right or wrong.</li>
            <li>We do <strong>not</strong> make decisions on dispute outcomes.</li>
            <li>Our role is to provide structure, resources, and facilitation to support both parties equally.</li>
            <li>Handshake does not benefit financially from how a dispute is resolved and remains independent of the outcome.</li>
          </ul>
        </div>

        <div>
          <p class="text-base text-text-primary font-semibold mb-2">Contact Handshake Support:</p>
          <p class="text-base text-text-primary mb-2">If you have questions or need assistance at any point:</p>
          <ul class="list-none pl-0 space-y-2 text-base text-text-primary">
            <li>
              <strong>WhatsApp: </strong> 
              <a href="https://wa.me/6580778295" target="_blank" rel="noopener noreferrer" class="text-blue-600 hover:underline">+65 8077 8295</a>
            </li>
            <li>
              <strong>Email: </strong> 
              <a href="mailto:contact&#64;handshake.finance" class="text-blue-600 hover:underline">contact&#64;handshake.finance</a>
            </li>
          </ul>
          <p class="text-base text-text-primary mt-2">We're here to guide you through the process.</p>
        </div>
      </div>
  </nz-collapse-panel>
</nz-collapse>
